import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { useEffect } from "react";

import { t } from "@/i18n-js/instance";
import type { Post } from "@/react/types/Post";
import type { Space } from "@/react/types/Space";

import { Comment } from "../Comment";
import { useJumpToComment } from "../useJumpToComment";
import { useCommentHierarchy } from "./useCommentHierarchy";

interface CommentHierarchyProps {
  autofocusReplyForm?: boolean;
  id: number;
  onCommentDelete: () => void;
  onReplyCreate: () => void;
  onReplyDelete: () => void;
  post: Post;
  showReplyForm: boolean;
  space: Space;
}

export const CommentHierarchy = ({
  space,
  post,
  id,
  showReplyForm = false,
  autofocusReplyForm = false,
  onCommentDelete,
  onReplyCreate,
  onReplyDelete,
}: CommentHierarchyProps) => {
  const { comment, isPending, updateQueryData } = useCommentHierarchy({
    postId: post.id,
    id,
    postCommentsClosed: post.is_comments_closed ?? false,
    postLikingDisabled: post.is_liking_disabled,
  });

  useEffect(() => {
    if (!isPending) {
      // Ensures that tooltips work in the engagement menu, which is currently
      // rendered on the BE.
      window.initialiseTippy?.();
    }
  }, [isPending]);

  useJumpToComment({ enabled: !isPending });

  if (isPending) {
    return <LoaderRow />;
  }

  if (!comment) {
    return (
      <div className="border-primary text-dark dark:text-lightest bg-v2-warning-100 rounded-b-md border-t p-2.5 py-4 text-md md:p-5">
        {t("comments.hierarchy.not_found")}
      </div>
    );
  }

  return (
    <Comment
      space={space}
      post={post}
      comment={comment}
      showReplyForm={showReplyForm}
      autofocusReplyForm={autofocusReplyForm}
      onUpdate={updateQueryData}
      onDelete={onCommentDelete}
      onReplyCreate={onReplyCreate}
      onReplyDelete={onReplyDelete}
    />
  );
};
